// =============================================================================
// PLATFORM-AGNOSTIC AUTH
// =============================================================================
// One dashboard, two platforms:
//  - Shopify: embedded app -> session token from App Bridge (window.shopify.idToken)
//  - WooCommerce: hosted dashboard -> session JWT bootstrapped from a one-time SSO
//    token the WP-admin button passes in the URL. The JWT is held in sessionStorage
//    and sent as the X-Trackful-Session header, which works in the embedded iframe
//    (where the tp_woo_session cookie is a blocked third-party cookie). The cookie
//    is still set server-side for the first-party new-tab flow.
// Every channel page goes through fetchApi() (hooks/api.ts), so abstracting the
// auth header here lets the same pages serve both platforms unchanged.
// =============================================================================

export type Platform = 'SHOPIFY' | 'WOOCOMMERCE';

const WOO_JWT_KEY = 'tp_woo_jwt';

let platform: Platform = 'SHOPIFY';
let wooSessionToken: string | null = null;

export function getPlatform(): Platform {
  return platform;
}

export function isWoo(): boolean {
  return platform === 'WOOCOMMERCE';
}

/** True when running inside an iframe (embedded in WP-admin). */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin access throws -> we are framed
  }
}

// Ad-platform login pages refuse to load inside an iframe, so when embedded we
// run OAuth in a new top-level tab. Open the blank tab SYNCHRONOUSLY in the click
// handler (before the await) so it is not blocked as a popup, then navigate it
// once the auth URL is fetched. The OAuth callback sets a first-party cookie so
// that new tab is authenticated for the asset-picker step.
export function openOAuthWindow(): Window | null {
  return isEmbedded() ? window.open('about:blank', '_blank') : null;
}

export function navigateOAuth(win: Window | null, url: string): void {
  if (win) {
    win.location.href = url;
  } else {
    window.location.href = url;
  }
}

/**
 * Run once at startup (before render). Detects the platform and, for WooCommerce,
 * obtains a session JWT: prefers a stored one (survives reloads), otherwise
 * exchanges the one-time SSO token from the URL.
 */
export async function initAuth(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const wooByParam = params.get('platform') === 'woocommerce';
  const wooBySession = sessionStorage.getItem('tp_platform') === 'woocommerce';

  if (!wooByParam && !wooBySession) {
    platform = 'SHOPIFY';
    return;
  }

  platform = 'WOOCOMMERCE';
  sessionStorage.setItem('tp_platform', 'woocommerce');

  // Prefer an already-stored JWT (reuse across reloads; avoids re-spending the
  // single-use SSO token).
  wooSessionToken = sessionStorage.getItem(WOO_JWT_KEY);

  const sso = params.get('sso');
  const siteId = params.get('siteId');
  // Only exchange the SSO token if we don't already hold a session JWT.
  if (sso && siteId && !wooSessionToken) {
    try {
      const res = await fetch('/api/woo/dashboard/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ siteId, token: sso }),
      });
      if (res.ok) {
        const data = (await res.json()) as { token?: string };
        if (data.token) {
          wooSessionToken = data.token;
          sessionStorage.setItem(WOO_JWT_KEY, data.token);
        }
      }
    } catch {
      // Ignored - subsequent API calls will 401 and the UI can prompt the
      // merchant to re-open the dashboard from WP-admin.
    }
  }

  // Strip the SSO token + siteId from the address bar / history.
  if (sso || siteId) {
    params.delete('sso');
    params.delete('siteId');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }
}

/**
 * Auth headers for an API request. Shopify -> Bearer id token; WooCommerce ->
 * X-Trackful-Session with the JWT (and the cookie travels too via
 * credentials:'include' for the first-party new-tab flow).
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (platform === 'WOOCOMMERCE') {
    return wooSessionToken ? { 'X-Trackful-Session': wooSessionToken } : {};
  }
  const token = await window.shopify?.idToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Clear the stored WooCommerce session JWT. Called when the API returns 401 so an
 * expired token does not get re-sent forever; the next full load (from WP-admin,
 * which carries a fresh SSO token) re-bootstraps the session.
 */
export function clearWooSession(): void {
  wooSessionToken = null;
  try {
    sessionStorage.removeItem(WOO_JWT_KEY);
  } catch {
    // ignore
  }
}
